import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { buildVisualizerUrl, PREVIEW_LINK_ATTRIBUTION } from '../../../common/utils/visualizer-link';
import { RoomWidgetId, RoomZone, VisualizerLayout } from './layout.schema';

/**
 * The editor's side of the live preview: the vendor's real visualizer in an
 * iframe, opened with `layoutPreview=1`, showing the draft being edited.
 *
 * Protocol (ai-visualizer `src/modules/Layout/layoutPreview.ts`), every
 * message an object with `source: 'ardealy-layout'`:
 *   here → preview  SET     { layout, edit, selected, screen }
 *   preview → here  READY   { screen }       loaded, or moved to another screen
 *                   SELECT  { key }          something was clicked in edit mode
 *                   MOVE    { id, zone, beforeId }   a widget was dropped
 *                   PANEL   { width }        the panel's edge was dragged
 *
 * The draft is never saved by the preview; it only ever arrives from here.
 */

export const LAYOUT_MESSAGE_SOURCE = 'ardealy-layout';
export type PreviewScreen = 'gallery' | 'room';

interface Options {
    alias: string | undefined;
    layout: VisualizerLayout;
    screen: PreviewScreen;
    edit: boolean;
    selected: string | null;
    onSelect: (key: string) => void;
    onMove: (id: RoomWidgetId, zone: RoomZone, beforeId: RoomWidgetId | null) => void;
    onPanelWidth: (width: number) => void;
}

export function useLayoutPreview({ alias, layout, screen, edit, selected, onSelect, onMove, onPanelWidth }: Options) {
    const iframeRef = useRef<HTMLIFrameElement | null>(null);
    const [ready, setReady] = useState(false);
    const [frameScreen, setFrameScreen] = useState<PreviewScreen | null>(null);
    const [reloadKey, setReloadKey] = useState(0);

    // The URL is fixed for the life of the frame: switching screens is a
    // message, so the preview does not reload (and lose its room) each time.
    const [initialScreen] = useState(screen);
    const src = useMemo(() => {
        if (!alias) return '';
        const base = buildVisualizerUrl({ alias, attribution: PREVIEW_LINK_ATTRIBUTION });
        if (!base) return '';
        const url = new URL(base, window.location.href);
        url.searchParams.set('layoutPreview', '1');
        url.searchParams.set('screen', initialScreen);
        return url.toString();
    }, [alias, initialScreen]);
    const origin = useMemo(() => {
        try { return src ? new URL(src).origin : ''; } catch { return ''; }
    }, [src]);

    const latest = useRef({ layout, screen, edit, selected, onSelect, onMove, onPanelWidth });
    latest.current = { layout, screen, edit, selected, onSelect, onMove, onPanelWidth };

    const send = useCallback(() => {
        const win = iframeRef.current?.contentWindow;
        if (!win || !origin) return;
        const { layout: l, screen: s, edit: e, selected: sel } = latest.current;
        win.postMessage({ source: LAYOUT_MESSAGE_SOURCE, type: 'SET', layout: l, screen: s, edit: e, selected: sel }, origin);
    }, [origin]);

    useEffect(() => {
        const onMessage = (event: MessageEvent) => {
            if (!iframeRef.current || event.source !== iframeRef.current.contentWindow) return;
            if (origin && event.origin !== origin) return;
            const data = event.data;
            if (!data || typeof data !== 'object' || data.source !== LAYOUT_MESSAGE_SOURCE) return;
            switch (data.type) {
                case 'READY':
                    setReady(true);
                    if (data.screen === 'gallery' || data.screen === 'room') setFrameScreen(data.screen);
                    send();
                    break;
                case 'SELECT':
                    if (typeof data.key === 'string') latest.current.onSelect(data.key);
                    break;
                case 'MOVE':
                    if (typeof data.id === 'string' && typeof data.zone === 'string') {
                        latest.current.onMove(data.id, data.zone, typeof data.beforeId === 'string' ? data.beforeId : null);
                    }
                    break;
                case 'PANEL':
                    if (typeof data.width === 'number' && Number.isFinite(data.width)) latest.current.onPanelWidth(data.width);
                    break;
            }
        };
        window.addEventListener('message', onMessage);
        return () => window.removeEventListener('message', onMessage);
    }, [origin, send]);

    // Every change to the draft, the screen or the mode goes straight to the frame.
    useEffect(() => {
        if (ready) send();
    }, [ready, layout, screen, edit, selected, send]);

    const reload = useCallback(() => {
        setReady(false);
        setFrameScreen(null);
        setReloadKey((k) => k + 1);
    }, []);

    return { iframeRef, src, ready, frameScreen, reload, reloadKey };
}
